import { mount, unmount } from 'redom';

import { filters, PencilBrush } from 'fabric';

import fileTypes from 'constants/file-types';

import MediaEditControls, { clipTypes, filterTypes } from 'generics/components/basic-text-editor-slate/elements/ht-file/media-edit-controls';

const maxBlur = 20;
const maxPixelation = 50;

let toolbar = null;

class ImageToolbar {

  static show(canvas, configuration) {

    const mountPoint = canvas.parentNode.parentNode;

    if (toolbar !== null) {
      unmount(mountPoint, toolbar);
    }

    mount(mountPoint, toolbar = new MediaEditControls(buildToobarConfiguration(configuration), configuration.anchor));

  }

  static hide(canvas) {

    const mountPoint = canvas.parentNode.parentNode;

    if (toolbar !== null) {
      unmount(toolbar.el.parentNode, toolbar);
    }

    toolbar = null;

  }

}

function determineClipType(activeObject) {

  return activeObject.clipType;

}

function setClip(canvas, activeObject, clip) {

  const image = activeObject;

  if (clip === clipTypes.SQUARE) {
    return canvas.setClipToSquare(image);
  } else if (clip === clipTypes.CIRCLE) {
    return canvas.setClipToCircle(image);
  } else if (clip === clipTypes.TRIANGLE) {
    return canvas.setClipToTriangle(image);
  } else if (clip === clipTypes.STAR) {
    return canvas.setClipToStar(image);
  }

}

function setFilter(activeObject, filterType, active) {

  const image = getImageObject(activeObject);

  if (filterType === filterTypes.GRAYSCALE) {
    image.filters = image.filters.filter(filter => !(filter instanceof filters.Grayscale));
    if (active === true) {
      image.filters.push(new filters.Grayscale());
    }
  } else if (filterType === filterTypes.SEPIA) {
    image.filters = image.filters.filter(filter => !(filter instanceof filters.Sepia));
    if (active === true) {
      image.filters.push(new filters.Sepia());
    }
  } else if (filterType === filterTypes.INVERT) {
    image.filters = image.filters.filter(filter => !(filter instanceof filters.Invert));
    if (active === true) {
      image.filters.push(new filters.Invert());
    }
  }

  image.applyFilters();

}

function getBrightness(activeObject) {

  const image = getImageObject(activeObject);

  const brightnessFilter = image.filters.find(filter => filter instanceof filters.Brightness);
  if (brightnessFilter) {
    return (brightnessFilter.brightness * 50) + 50;
  }
  return 50;

}

function setBrightness(activeObject, brightnessPercentage) {

  const image = getImageObject(activeObject);

  let brightnessFilter = image.filters.find(filter => filter instanceof filters.Brightness);
  if (!brightnessFilter) {
    brightnessFilter = new filters.Brightness({ brightness: 0 });
    image.filters.push(brightnessFilter);
  }
  brightnessFilter.brightness = (brightnessPercentage - 50) / 50;
  image.applyFilters();

}

function getContrast(activeObject) {

  const image = getImageObject(activeObject);

  const contrastFilter = image.filters.find(filter => filter instanceof filters.Contrast);
  if (contrastFilter) {
    return (contrastFilter.contrast * 50) + 50;
  }
  return 50; // Default to 50 if no filter is applied (no change)
}

function setContrast(activeObject, contrastPercentage) {

  const image = getImageObject(activeObject);

  let contrastFilter = image.filters.find(filter => filter instanceof filters.Contrast);
  if (!contrastFilter) {
    contrastFilter = new filters.Contrast({ contrast: 0 });
    image.filters.push(contrastFilter);
  }
  contrastFilter.contrast = (contrastPercentage - 50) / 50;
  image.applyFilters();

}

function getSaturation(activeObject) {

  const image = getImageObject(activeObject);

  const saturationFilter = image.filters.find(filter => filter instanceof filters.Saturation);
  if (saturationFilter) {
    return (saturationFilter.saturation * 50) + 50;
  }
  return 50; // Default to 50 if no filter is applied (no change)
}

function setSaturation(activeObject, saturationPercentage) {

  const image = getImageObject(activeObject);

  let saturationFilter = image.filters.find(filter => filter instanceof filters.Saturation);
  if (!saturationFilter) {
    saturationFilter = new filters.Saturation({ saturation: 0 });
    image.filters.push(saturationFilter);
  }
  saturationFilter.saturation = (saturationPercentage - 50) / 50;
  image.applyFilters();
}

function getHue(activeObject) {

  const image = getImageObject(activeObject);

  const hueFilter = image.filters.find(filter => filter instanceof filters.HueRotation);
  if (hueFilter) {
    return (hueFilter.rotation / (2 * Math.PI)) * 100; // Convert radians to percentage
  }
  return 0;
}

function setHue(activeObject, huePercentage) {

  const image = getImageObject(activeObject);

  let hueFilter = image.filters.find(filter => filter instanceof filters.HueRotation);
  if (!hueFilter) {
    hueFilter = new filters.HueRotation({ rotation: 0 });
    image.filters.push(hueFilter);
  }
  hueFilter.rotation = (huePercentage / 100) * 2 * Math.PI; // Convert percentage to radians
  image.applyFilters();
}

function getBlur(activeObject) {

  const image = getImageObject(activeObject);

  const blurFilter = image.filters.find(filter => filter instanceof filters.Blur);
  if (blurFilter) {
    return (blurFilter.blur / maxBlur) * 100;
  }
  return 0; // Default to 0 if no filter is applied
}

function setBlur(activeObject, blurPercentage) {

  const image = getImageObject(activeObject);

  let blurFilter = image.filters.find(filter => filter instanceof filters.Blur);
  if (!blurFilter) {
    blurFilter = new filters.Blur({ blur: 0 });
    image.filters.push(blurFilter);
  }
  blurFilter.blur = (blurPercentage / 100) * maxBlur; // Convert percentage to pixel value
  image.applyFilters();

}

function getOpacity(activeObject) {

  return activeObject.opacity * 100;

}

function setOpacity(activeObject, opacityPercentage) {

  activeObject.opacity = opacityPercentage / 100;
  activeObject.set({ opacity: activeObject.opacity });

}

function getPixelation(activeObject) {

  const image = getImageObject(activeObject);

  const pixelateFilter = image.filters.find(filter => filter instanceof filters.Pixelate);
  if (pixelateFilter) {
    return (pixelateFilter.size / maxPixelation) * 100;
  }
  return 0;
}

function setPixelation(activeObject, pixelationPercentage) {

  const image = getImageObject(activeObject);

  let pixelateFilter = image.filters.find(filter => filter instanceof filters.Pixelate);
  if (!pixelateFilter) {
    pixelateFilter = new filters.Pixelate({ size: 1 });
    image.filters.push(pixelateFilter);
  }
  pixelateFilter.size = (pixelationPercentage / 100) * maxPixelation;
  image.applyFilters();

}

function getImageObject(activeObject) {

  if (activeObject.type === 'image') {
    return activeObject;
  } else if (activeObject.groupType === 'image') {
    const imageObject = activeObject.getObjects().find(object => object.type === 'image');
    return imageObject || null;
  }

  return null;

}

function determineImageURL(activeObject) {

  const image = getImageObject(activeObject);

  if (image === null) {
    return null;
  }

  return image.getElement().src;

}

function buildToobarConfiguration(configuration) {

  const canvas = configuration.canvas;
  const fabricCanvas = configuration.fabricCanvas;
  const activeObject = configuration.activeObject;

  const image = getImageObject(activeObject);
  const imageURL = determineImageURL(activeObject);

  function postChange(object) {
    fabricCanvas.renderAll();
    fabricCanvas.fire('object:modified', { target: object || activeObject });
  }

  const toolbarConfiguration = {
    canvas,
    allowGIFs: false,
    url: imageURL,
    editorMainReference: { current: configuration.canvas.el.closest('.basic-text-editor-main') },
    mediaType: fileTypes.IMAGE,
    onFile: async (file) => {
      const object = await configuration.canvas.replaceImage(file, activeObject);
      postChange(object);
    },
    onURLChange: async (url) => {
      const object = await configuration.canvas.replaceImageWithURL(url, activeObject);
      postChange(object);
    },
    disableAltText: true,
    disableAlignmentControls: true,
    displayPositionControlPanel: true,
    clip: determineClipType(activeObject),
    disableBrushControls: false,
    onClipChange: async (clip) => {
      const object = await setClip(configuration.canvas, activeObject, clip);
      postChange(object);
    },
    onMoveForward() {
      fabricCanvas.bringObjectForward(activeObject);
      postChange();
    },
    onMoveBackward() {
      fabricCanvas.sendObjectBackwards(activeObject);
      postChange();
    },
    onMoveToFront() {
      fabricCanvas.bringObjectToFront(activeObject);
      postChange();
    },
    onMoveToBack() {
      fabricCanvas.sendObjectToBack(activeObject);
      postChange();
    },
    filter: {
      filterType: activeObject.filterType,
      brightness: getBrightness(activeObject),
      contrast: getContrast(activeObject),
      saturation: getSaturation(activeObject),
      hue: getHue(activeObject),
      blur: getBlur(activeObject),
      opacity: getOpacity(activeObject),
      pixelate: getPixelation(activeObject),
      onTypeChange: (filterType, active) => {
        setFilter(activeObject, filterType, active);
        postChange();
      },
      onBrightnessChange: (brightness) => {
        setBrightness(activeObject, brightness);
        postChange();
      },
      onContrastChange: (contrast) => {
        setContrast(activeObject, contrast);
        postChange();
      },
      onSaturationChange: (saturation) => {
        setSaturation(activeObject, saturation);
        postChange();
      },
      onHueChange: (hue) => {
        setHue(activeObject, hue);
        postChange();
      },
      onBlurChange: (blur) => {
        setBlur(activeObject, blur);
        postChange();
      },
      onOpacityChange: (opacity) => {
        setOpacity(activeObject, opacity);
        postChange();
      },
      onPixelationChange: (pixelation) => {
        setPixelation(activeObject, pixelation);
        postChange();
      },
      onRemoveAllFilters: () => {
        activeObject.filters = [];
        activeObject.applyFilters();
        postChange();
      }
    },
    onClose() {
      canvas.enableDeleting();
    },
    onActive() {
      canvas.disableDeleting();
    },
    onInactive() {
      canvas.enableDeleting();
    },
    onDuplicate() {
      canvas.duplicateActiveObject();
    },
    onCrop() {
      canvas.cropImage(activeObject);
    }
  };

  return toolbarConfiguration;

}

export default ImageToolbar;